import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Send, CheckCircle, ArrowRight, Lock } from "lucide-react";

type FormData = {
  nom: string; prenom: string; email: string; telephone: string;
  adresse: string; ville: string; cp: string;
  formation: string; financement: string; message: string;
};

const INITIAL: FormData = {
  nom: "", prenom: "", email: "", telephone: "",
  adresse: "", ville: "", cp: "",
  formation: "", financement: "", message: "",
};

const STRIPE_LINKS: Record<string, string> = {
  passerelle: "https://buy.stripe.com/00w3cv6776rE4gn0UudZ605",
};

export default function Inscription() {
  const [form, setForm] = useState<FormData>(INITIAL);
  const [step, setStep] = useState<"form" | "confirm" | "done">("form");

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "13px 16px", borderRadius: 10,
    background: "#1a1a1a", border: "1px solid #2a2a2a",
    color: "#f5f5f5", fontSize: 14, outline: "none", boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    display: "block", fontSize: 13, fontWeight: 600, color: "#9ca3af", marginBottom: 8,
  };

  const showStripe = form.formation === "passerelle" && form.financement === "stripe";

  if (step === "done") {
    return (
      <div style={{ background: "#0a0a0a", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ maxWidth: 520, textAlign: "center" }}>
          <CheckCircle size={64} color="#16a34a" style={{ margin: "0 auto 24px" }} />
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#fff", marginBottom: 16 }}>Inscription envoyée !</h1>
          <p style={{ color: "#9ca3af", fontSize: 16, lineHeight: 1.7, marginBottom: 32 }}>
            Merci <strong style={{ color: "#fff" }}>{form.prenom} {form.nom}</strong> !<br />
            Votre demande pour la formation <strong style={{ color: "#60a5fa" }}>{form.formation}</strong> a bien été reçue.<br />
            Nous vous contacterons sous 24h à l'adresse <strong style={{ color: "#60a5fa" }}>{form.email}</strong>.
          </p>
          {showStripe && (
            <a
              href={STRIPE_LINKS.passerelle}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "14px 28px", borderRadius: 10,
                background: "linear-gradient(135deg, #1d4ed8, #2563eb)", color: "#fff",
                fontWeight: 700, fontSize: 15, textDecoration: "none",
                boxShadow: "0 6px 20px rgba(37,99,235,0.35)", marginBottom: 16,
              }}
            >
              <CreditCard size={18} /> Procéder au paiement — 499 €
            </a>
          )}
          <div>
            <Link to="/" style={{ color: "#2563eb", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#0a0a0a", color: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(145deg, #0a0a0a, #0d1a2e)",
        borderBottom: "1px solid #1e3a5f",
        padding: "56px 24px 48px", textAlign: "center",
      }}>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 42px)", fontWeight: 800, color: "#fff", marginBottom: 12 }}>
          Formulaire d'Inscription
        </h1>
        <p style={{ color: "#9ca3af", fontSize: 15, maxWidth: 480, margin: "0 auto" }}>
          Remplissez ce formulaire pour vous inscrire à une formation. Nous vous contacterons sous 24h pour confirmer.
        </p>
      </div>

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "48px 24px" }}>
        <div style={{ background: "#111", borderRadius: 20, border: "1px solid #1e1e1e", padding: 36 }}>
          <form onSubmit={(e) => { e.preventDefault(); setStep("done"); }}>
            {/* Identité */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
                01 — Identité
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Nom *</label>
                  <input name="nom" value={form.nom} onChange={handle} required placeholder="Votre nom" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Prénom *</label>
                  <input name="prenom" value={form.prenom} onChange={handle} required placeholder="Votre prénom" style={inputStyle} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handle} required placeholder="email@exemple.fr" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Téléphone *</label>
                  <input name="telephone" value={form.telephone} onChange={handle} required placeholder="06 XX XX XX XX" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Adresse */}
            <div style={{ marginBottom: 28, borderTop: "1px solid #1e1e1e", paddingTop: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
                02 — Adresse
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Adresse</label>
                <input name="adresse" value={form.adresse} onChange={handle} placeholder="Numéro et rue" style={inputStyle} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Code postal</label>
                  <input name="cp" value={form.cp} onChange={handle} placeholder="75000" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Ville</label>
                  <input name="ville" value={form.ville} onChange={handle} placeholder="Paris" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Formation */}
            <div style={{ marginBottom: 28, borderTop: "1px solid #1e1e1e", paddingTop: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
                03 — Formation
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Formation choisie *</label>
                <select name="formation" value={form.formation} onChange={handle} required style={{ ...inputStyle, appearance: "none" as const }}>
                  <option value="">Sélectionnez une formation</option>
                  <option value="Formation CCPCT (Taxi)">Formation Initiale CCPCT — Sur devis</option>
                  <option value="passerelle">Passerelle VTC → Taxi (14h) — 499 € TTC</option>
                  <option value="Formation Continue">Formation Continue — Sur devis</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Financement envisagé</label>
                <select name="financement" value={form.financement} onChange={handle} style={{ ...inputStyle, appearance: "none" as const }}>
                  <option value="">Sélectionnez...</option>
                                                      
                  <option value="stripe">Paiement en ligne — Carte bancaire (Stripe)</option>
                  <option value="virement">Virement bancaire</option>
                  <option value="autre">Autre / À définir</option>
                </select>
              </div>
            </div>

            {/* Stripe notice */}
            {showStripe && (
              <div style={{
                marginBottom: 24, borderRadius: 12, padding: 18,
                background: "rgba(37,99,235,0.1)", border: "1px solid rgba(37,99,235,0.3)",
                display: "flex", alignItems: "flex-start", gap: 12,
              }}>
                <CreditCard size={20} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontWeight: 700, color: "#60a5fa", fontSize: 14, marginBottom: 4 }}>
                    Paiement sécurisé — 499 € TTC
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 13 }}>
                    Après validation du formulaire, vous serez redirigé vers la page de paiement Stripe sécurisée.
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            <div style={{ marginBottom: 28, borderTop: "1px solid #1e1e1e", paddingTop: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>
                04 — Message (optionnel)
              </div>
              <textarea
                name="message" value={form.message} onChange={handle}
                rows={3} placeholder="Disponibilités, questions, situation particulière..."
                style={{ ...inputStyle, resize: "vertical" as const }}
              />
            </div>

            {/* Submit */}
            <button type="submit" style={{
              width: "100%", padding: "16px", borderRadius: 12,
              background: "linear-gradient(135deg, #1d4ed8, #2563eb)", color: "#fff",
              fontWeight: 800, fontSize: 16, border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
            }}>
              {showStripe ? <><CreditCard size={18} /> Valider et payer — 499 €</> : <><Send size={17} /> Envoyer mon inscription <ArrowRight size={16} /></>}
            </button>

            <p style={{ textAlign: "center", color: "#4b5563", fontSize: 11, marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <Lock size={11} /> Données sécurisées et confidentielles — SKOOL FORMATION SASU
            </p>
          </form>
        </div>

        {/* Lien contact */}
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <span style={{ color: "#6b7280", fontSize: 14 }}>Vous préférez nous contacter directement ? </span>
          <Link to="/contact" style={{ color: "#2563eb", fontWeight: 600, fontSize: 14, textDecoration: "none" }}>
            Page Contact →
          </Link>
        </div>
      </div>

      <style>{`
        input:focus, textarea:focus, select:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
        }
        select option { background: #1a1a1a; }
        @media (max-width: 520px) {
          .grid-2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
